import { Info } from 'lucide-react';
import { useId, useState } from 'react';

import { useTranslation } from '@/i18n';

interface TaxLineProps {
    /** Tax as the POS reported it. Never derived, and never the only thing in the row. */
    taxCents: number;

    /** The digital service fee, or 0 at a location that charges none. */
    digitalSurchargeCents: number;

    money: (minorUnits: number) => string;
}

/**
 * The tax row, which is a fee row too wherever a location charges one.
 *
 * Two shapes, decided by one number:
 *
 * A location with no digital service fee gets "Tax" and the POS's tax figure, exactly as before. That is most
 * of them, and nothing about the row changes.
 *
 * A location that charges one gets "Taxes and Fees", the two figures added, and an information button that
 * discloses what the fee was. The addition is for display and nothing is derived from it — both figures stay
 * separate everywhere they are stored, because a guest who is charged a fee is entitled to be told what it
 * was, and a summed number cannot be taken apart again.
 *
 * The disclosure is inline rather than floating. A guest reading this is holding a phone in a restaurant, and
 * a tooltip that hovers has nothing to hover on; a panel that opens in place pushes the rows below it down,
 * which is a thing that visibly happened rather than a thing that might be underneath a finger. It is also
 * why this is not a dialog: the app opens those only when a decision is needed, and there is no decision here.
 */
export function TaxLine({ taxCents, digitalSurchargeCents, money }: TaxLineProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const disclosureId = useId();

    if (digitalSurchargeCents <= 0) {
        return (
            <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">{t('check.tax')}</dt>
                <dd>{money(taxCents)}</dd>
            </div>
        );
    }

    return (
        <>
            <div className="flex justify-between gap-3">
                <dt className="flex min-w-0 items-center gap-1.5 text-muted-foreground">
                    {t('check.taxesAndFees')}

                    {/*
                      * Sized in em rather than a fixed pixel height, so it tracks the row's type. A guest who
                      * has scaled their text up gets a bigger icon with it rather than a shrinking dot.
                      *
                      * The tap target is padded well past the glyph - 46px around a 14px icon. 44 is the floor for
                      * a thumb, and this one sits in a line of small print on a phone. The negative margin
                      * keeps that padding from moving the row it is in.
                      */}
                    <button
                        type="button"
                        onClick={() => setOpen((wasOpen) => !wasOpen)}
                        aria-expanded={open}
                        aria-controls={disclosureId}
                        aria-label={t('check.feeInfoLabel')}
                        className="-m-4 inline-flex items-center justify-center rounded-full p-4 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                    >
                        <Info aria-hidden="true" className="size-[1em]" />
                    </button>
                </dt>
                <dd className="shrink-0">{money(taxCents + digitalSurchargeCents)}</dd>
            </div>

            {/*
              * Rendered only when open, rather than hidden with CSS. A closed disclosure that is still in the
              * accessibility tree reads the fee out to a screen-reader user in the middle of the totals, which
              * is the one place a stray sentence about money is most confusing.
              */}
            {open && (
                <p id={disclosureId} className="-mt-0.5 text-xs leading-relaxed text-muted-foreground">
                    {t('check.feeExplanation', { amount: money(digitalSurchargeCents) })}
                </p>
            )}
        </>
    );
}
